// Service Steady PWA — src/components/WeatherDetailModal.tsx — v3.2 — 2026-06-04
//
// v3.2 changes:
//   - Body scroll lock uses iOS PWA-safe position:fixed pattern
//     (useBodyScrollLock hook), since plain overflow:hidden doesn't work.
//   - Mobile sheet sized via 100dvh (not 100vh) so iOS PWA viewport math
//     is correct, and inset above the TabBar so it never sits behind the
//     bottom nav.
//   - Scroll container has overscroll-behavior: contain so a downward
//     drag at the bottom of the modal can't bubble out and scroll the
//     page underneath.
//   - Per-day drill-down: tap any row in the 10-day list to expand a
//     panel with that day's hourly forecast + sun/UV/wind/precip details.
//
// Sections (scrollable):
//   1. Hero (gradient continuation): big temp, condition, feels-like, chips
//   2. Hourly Today — horizontal scroll, 24 cells
//   3. 10-Day Forecast — expandable rows (chevron rotates)
//   4. Today details — 2-col grid
//   5. Air Quality — AQI + PM2.5/PM10/O3/NO2

import { useState } from 'react';
import {
  ChevronDown,
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudRain,
  CloudSnow,
  Droplets,
  Sun,
  Sunrise,
  Sunset,
  Wind,
  X,
  Zap,
} from 'lucide-react';
import { useWeatherDetail } from '@/hooks/useWeather';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { getGradient, aqiBadgeClasses, aqiLabel } from '@/lib/weatherStyle';
import { daypartsFromDetail, type DaypartPrecip } from '@/lib/weatherDayparts';
import type {
  WeatherCategory,
  WeatherDailyEntry,
  WeatherHourly,
} from '@/lib/api/weather';

// ---------------------------------------------------------------------------
// Icon + time helpers
// ---------------------------------------------------------------------------

function IconFor({
  category,
  className,
}: {
  category: WeatherCategory;
  className?: string;
}) {
  const c = className ?? 'h-5 w-5';
  switch (category) {
    case 'clear':
      return <Sun className={c} />;
    case 'partly_cloudy':
      return <Cloud className={c} />;
    case 'cloudy':
      return <Cloud className={c} />;
    case 'rain':
      return <CloudRain className={c} />;
    case 'snow':
      return <CloudSnow className={c} />;
    case 'thunder':
      return <Zap className={c} />;
    case 'fog':
      return <CloudFog className={c} />;
    default:
      return <Cloud className={c} />;
  }
}

void CloudDrizzle;

function formatHour(iso: string): string {
  if (!iso) return '';
  const m = /T(\d{2}):/.exec(iso);
  if (!m || !m[1]) return '';
  let h = parseInt(m[1], 10);
  const suffix = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}${suffix}`;
}

function formatTimeOfDay(iso: string): string {
  if (!iso) return '';
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m || !m[1] || !m[2]) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const suffix = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min}${suffix}`;
}

// ---------------------------------------------------------------------------
// Hourly cell + daily row + drill-down
// ---------------------------------------------------------------------------

function HourlyCell({
  entry,
  isNow,
}: {
  entry: WeatherHourly;
  isNow: boolean;
}) {
  return (
    <div
      className={`flex w-14 flex-shrink-0 flex-col items-center gap-1 rounded-lg p-2 text-center text-xs ${
        isNow ? 'bg-white/25 text-white' : 'bg-white/10 text-white/90'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide opacity-90">
        {isNow ? 'Now' : formatHour(entry.time)}
      </div>
      <IconFor category={entry.category} className="h-4 w-4 opacity-90" />
      <div className="text-sm font-semibold">{entry.temp_f}°</div>
      <div className="text-[10px] opacity-75">{entry.precip_chance_pct}%</div>
    </div>
  );
}

function DailyRow({
  entry,
  hiMin,
  hiMax,
  isExpanded,
}: {
  entry: WeatherDailyEntry;
  hiMin: number;
  hiMax: number;
  isExpanded: boolean;
}) {
  const lo = entry.low_f ?? 0;
  const hi = entry.high_f ?? 0;
  const range = Math.max(1, hiMax - hiMin);
  const startPct = ((lo - hiMin) / range) * 100;
  const widthPct = ((hi - lo) / range) * 100;
  return (
    <div className="flex items-center gap-3 py-2.5">
      <ChevronDown
        className={`h-3 w-3 shrink-0 text-ink-400 transition-transform dark:text-[#64748B] ${
          isExpanded ? 'rotate-0' : '-rotate-90'
        }`}
      />
      <div className="w-16 text-sm font-medium text-ink-900 dark:text-white">
        {entry.day_name}
      </div>
      <IconFor
        category={entry.category}
        className="h-5 w-5 text-ink-700 dark:text-[#CBD5E1]"
      />
      <div className="flex w-10 items-center justify-end text-xs text-blue-500 dark:text-blue-400">
        {entry.precip_chance_pct}%
      </div>
      <div className="flex flex-1 items-center gap-2">
        <div className="w-8 text-right text-xs text-ink-600 dark:text-[#94A3B8]">
          {entry.low_f !== null ? `${entry.low_f}°` : '—'}
        </div>
        <div className="relative h-1.5 flex-1 rounded-full bg-ink-100 dark:bg-[#1E293B]">
          <div
            className="absolute top-0 h-1.5 rounded-full bg-gradient-to-r from-blue-400 via-yellow-400 to-orange-500"
            style={{
              left: `${Math.max(0, startPct)}%`,
              width: `${Math.max(4, widthPct)}%`,
            }}
          />
        </div>
        <div className="w-8 text-xs text-ink-800 dark:text-white">
          {entry.high_f !== null ? `${entry.high_f}°` : '—'}
        </div>
      </div>
    </div>
  );
}

/** Expansion panel rendered below an expanded daily row. v3.2. */
function DayDetailPanel({
  entry,
  hourly,
  gradient,
  isToday,
}: {
  entry: WeatherDailyEntry;
  hourly: WeatherHourly[];
  gradient: string;
  isToday: boolean;
}) {
  // For today's drill-down, drop past hours so the panel matches "what's
  // ahead the rest of today" rather than echoing midnight.
  const cells = isToday
    ? hourly.filter((h) => {
        const m = /T(\d{2}):/.exec(h.time);
        const hr = m?.[1] ? parseInt(m[1], 10) : 0;
        return hr >= new Date().getHours();
      })
    : hourly;

  return (
    <div className="mb-2 mt-1 rounded-xl border border-ink-100 bg-ink-50 p-3 dark:border-[#1F2A37] dark:bg-[#1E293B]">
      {/* Hourly horizontal scroll */}
      {cells.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className={`flex gap-2 rounded-xl bg-gradient-to-br ${gradient} p-2`}>
            {cells.map((h, i) => (
              <HourlyCell key={h.time} entry={h} isNow={isToday && i === 0} />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-white px-3 py-2 text-xs text-ink-500 dark:bg-[#0F172A] dark:text-[#94A3B8]">
          No hourly data for this day
        </div>
      )}

      {/* Per-day detail grid */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
        <PanelTile
          icon={<Sunrise className="h-3.5 w-3.5" />}
          label="Sunrise"
          value={entry.sunrise ? formatTimeOfDay(entry.sunrise) : '—'}
        />
        <PanelTile
          icon={<Sunset className="h-3.5 w-3.5" />}
          label="Sunset"
          value={entry.sunset ? formatTimeOfDay(entry.sunset) : '—'}
        />
        <PanelTile
          icon={<Sun className="h-3.5 w-3.5" />}
          label="UV max"
          value={entry.uv_index !== null ? String(entry.uv_index) : '—'}
        />
        <PanelTile
          icon={<CloudRain className="h-3.5 w-3.5" />}
          label="Precip"
          value={
            entry.precip_inches !== null
              ? `${entry.precip_inches.toFixed(2)} in`
              : '—'
          }
        />
        <PanelTile
          icon={<Wind className="h-3.5 w-3.5" />}
          label="Wind max"
          value={entry.wind_max_mph !== null ? `${entry.wind_max_mph} mph` : '—'}
        />
        <PanelTile
          icon={<Cloud className="h-3.5 w-3.5" />}
          label="Chance"
          value={`${entry.precip_chance_pct}%`}
        />
      </div>
    </div>
  );
}

function PanelTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg bg-white px-2 py-1.5 dark:bg-[#0F172A]">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-500 dark:text-[#94A3B8]">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm font-semibold text-ink-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
}

export function WeatherDetailModal({ open, onClose }: Props) {
  const { data, isLoading, isError } = useWeatherDetail(open);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  // iOS-safe body scroll lock.
  useBodyScrollLock(open);

  if (!open) return null;

  const okData = data && data.ok ? data : null;
  const cat: WeatherCategory = okData?.current.category ?? 'cloudy';
  const isDay = okData?.current.is_day ?? false;
  const gradient = getGradient(cat, isDay);

  const dailyAll = okData?.daily ?? [];
  const hourlyByDay = okData?.hourly_by_day ?? {};
  const allLows = dailyAll
    .map((d) => d.low_f)
    .filter((v): v is number => v !== null);
  const allHighs = dailyAll
    .map((d) => d.high_f)
    .filter((v): v is number => v !== null);
  const hiMin = allLows.length > 0 ? Math.min(...allLows) : 0;
  const hiMax = allHighs.length > 0 ? Math.max(...allHighs) : 0;

  const hourlyAll = okData?.hourly_today ?? [];
  // 4-daypart precip breakdown (Morning/Afternoon/Evening/Overnight). Derived
  // from the same hourly data the modal already holds. Aggregation = peak
  // hour per window; Overnight is forward-looking (the coming night). See
  // lib/weatherDayparts.ts.
  const dayparts: DaypartPrecip[] = okData ? daypartsFromDetail(okData) : [];

  return (
    <div
      // The outer wrapper uses pb-[var(--tab-h,64px)] to reserve space for
      // the bottom TabBar so the sheet never extends behind it. dvh is
      // used so iOS PWA address-bar/keyboard changes don't break layout.
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      style={{
        paddingBottom: 'var(--tab-h, 64px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Weather details"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl dark:bg-[#0F172A] sm:rounded-3xl"
        style={{
          // 100dvh - tab bar - top safe area. Constrains the sheet so
          // every section is reachable above the TabBar.
          maxHeight:
            'calc(100dvh - var(--tab-h, 64px) - env(safe-area-inset-top, 0px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HERO — gradient continuation */}
        <div
          className={`relative shrink-0 bg-gradient-to-br ${gradient} px-5 pb-5 pt-4 text-white`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-80">
                {okData?.location.name ?? 'Loading…'}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-6xl font-thin leading-none -tracking-tight">
                  {okData ? `${okData.current.temp_f}°` : '—'}
                </div>
              </div>
              <div className="mt-1 text-sm opacity-90">
                {okData?.current.condition_short ?? ' '}
              </div>
              {okData ? (
                <div className="mt-0.5 text-xs opacity-75">
                  Feels like {okData.current.feels_like_f}°
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {okData ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs backdrop-blur-sm">
                <Wind className="h-3 w-3" />
                {okData.current.wind_mph} mph {okData.current.wind_direction}
              </span>
              <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs backdrop-blur-sm">
                {okData.current.humidity_pct}% humidity
              </span>
              {okData.today.high_f !== null && okData.today.low_f !== null ? (
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs backdrop-blur-sm">
                  H {okData.today.high_f}° L {okData.today.low_f}°
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* SCROLLABLE BODY */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4 [overscroll-behavior:contain] [-webkit-overflow-scrolling:touch]"
          style={{
            // Generous bottom padding so the last AQI tile never sits
            // flush against the sheet's edge. Bigger than safe-area
            // inset since the sheet itself sits above the TabBar.
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)',
          }}
        >
          {isError || (data && !data.ok) ? (
            <div className="py-8 text-center text-sm text-ink-700 dark:text-[#CBD5E1]">
              Detail forecast unavailable
            </div>
          ) : (
            <>
              {/* HOURLY TODAY */}
              <section>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  Hourly today
                </h3>
                {isLoading || hourlyAll.length === 0 ? (
                  <div className="flex gap-2 overflow-hidden">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-20 w-14 animate-pulse rounded-lg bg-ink-100 dark:bg-[#1E293B]"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <div
                      className={`flex gap-2 rounded-xl bg-gradient-to-br ${gradient} p-2`}
                    >
                      {hourlyAll.map((h, i) => (
                        <HourlyCell key={h.time} entry={h} isNow={i === 0} />
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* RAIN BY PART OF DAY — 4-daypart precip breakdown. Peak hour
                  per window (see lib/weatherDayparts.ts); Overnight is the
                  coming night. Current block highlighted, elapsed blocks
                  dimmed. */}
              <section className="mt-6">
                <h3
                  className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]"
                  title="Peak hourly chance of rain in each part of the day"
                >
                  Rain by part of day
                </h3>
                {isLoading || dayparts.length === 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-[68px] animate-pulse rounded-lg bg-ink-100 dark:bg-[#1E293B]"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {dayparts.map((dp) => (
                      <DaypartTile key={dp.key} dp={dp} />
                    ))}
                  </div>
                )}
              </section>

              {/* 10-DAY with drill-down */}
              <section className="mt-6">
                <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  10-day forecast
                </h3>
                {isLoading || dailyAll.length === 0 ? (
                  <div className="space-y-1.5">
                    {Array.from({ length: 7 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-8 animate-pulse rounded bg-ink-100 dark:bg-[#1E293B]"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="divide-y divide-ink-100 dark:divide-[#1F2A37]">
                    {dailyAll.map((d) => {
                      const isExpanded = expandedDay === d.date;
                      const isToday = d.day_name === 'Today';
                      return (
                        <div key={d.date}>
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedDay(isExpanded ? null : d.date)
                            }
                            className="block w-full text-left active:bg-ink-50 dark:active:bg-[#1E293B]"
                            aria-expanded={isExpanded}
                          >
                            <DailyRow
                              entry={d}
                              hiMin={hiMin}
                              hiMax={hiMax}
                              isExpanded={isExpanded}
                            />
                          </button>
                          {isExpanded ? (
                            <DayDetailPanel
                              entry={d}
                              hourly={hourlyByDay[d.date] ?? []}
                              gradient={gradient}
                              isToday={isToday}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* TODAY DETAILS */}
              <section className="mt-6">
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  Today details
                </h3>
                {isLoading || !okData ? (
                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-14 animate-pulse rounded-lg bg-ink-100 dark:bg-[#1E293B]"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <DetailTile
                      icon={<Sunrise className="h-4 w-4" />}
                      label="Sunrise"
                      value={
                        okData.today.sunrise
                          ? formatTimeOfDay(okData.today.sunrise)
                          : '—'
                      }
                    />
                    <DetailTile
                      icon={<Sunset className="h-4 w-4" />}
                      label="Sunset"
                      value={
                        okData.today.sunset
                          ? formatTimeOfDay(okData.today.sunset)
                          : '—'
                      }
                    />
                    <DetailTile
                      icon={<Sun className="h-4 w-4" />}
                      label="UV index"
                      value={
                        okData.today.uv_index !== null
                          ? String(okData.today.uv_index)
                          : '—'
                      }
                    />
                    <DetailTile
                      icon={<Cloud className="h-4 w-4" />}
                      label="Humidity"
                      value={`${okData.current.humidity_pct}%`}
                    />
                    <DetailTile
                      icon={<Wind className="h-4 w-4" />}
                      label="Wind"
                      value={`${okData.current.wind_mph} mph ${okData.current.wind_direction}`}
                    />
                    <DetailTile
                      icon={<Wind className="h-4 w-4" />}
                      label="Gusts"
                      value={`${okData.current.wind_gusts_mph} mph`}
                    />
                  </div>
                )}
              </section>

              {/* AIR QUALITY */}
              <section className="mt-6">
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  Air quality
                </h3>
                {isLoading ? (
                  <div className="h-24 animate-pulse rounded-lg bg-ink-100 dark:bg-[#1E293B]" />
                ) : okData?.air_quality ? (
                  <div className="rounded-xl border border-ink-100 bg-white p-3 dark:border-[#1F2A37] dark:bg-[#0F172A]">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl font-semibold text-ink-900 dark:text-white">
                        {okData.air_quality.us_aqi ?? '—'}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-ink-500 dark:text-[#94A3B8]">
                          US AQI
                        </div>
                        <span
                          className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${aqiBadgeClasses(
                            okData.air_quality.category,
                          )}`}
                        >
                          {aqiLabel(okData.air_quality.category)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                      <PollutantTile
                        label="PM2.5"
                        value={okData.air_quality.pm2_5}
                        unit="µg/m³"
                      />
                      <PollutantTile
                        label="PM10"
                        value={okData.air_quality.pm10}
                        unit="µg/m³"
                      />
                      <PollutantTile
                        label="O₃"
                        value={okData.air_quality.o3}
                        unit="µg/m³"
                      />
                      <PollutantTile
                        label="NO₂"
                        value={okData.air_quality.no2}
                        unit="µg/m³"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-600 dark:bg-[#1E293B] dark:text-[#94A3B8]">
                    Air quality unavailable
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-tile components
// ---------------------------------------------------------------------------

function DetailTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-ink-100 bg-white p-2.5 dark:border-[#1F2A37] dark:bg-[#0F172A]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-ink-500 dark:text-[#94A3B8]">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm font-semibold text-ink-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

function DaypartTile({ dp }: { dp: DaypartPrecip }) {
  // Sublabel orients the block: the current one reads "Now"; the forward
  // Overnight reads "tonight"; others stay blank (line reserved for stable
  // height across the 4-up row).
  const sub = dp.isCurrent ? 'Now' : dp.isTomorrow ? 'tonight' : '';
  return (
    <div
      className={[
        'flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-center transition-colors',
        dp.isCurrent
          ? 'border-ss-blue/40 bg-ss-blue/5 dark:border-ss-blue/40 dark:bg-ss-blue/10'
          : 'border-ink-100 bg-white dark:border-[#1F2A37] dark:bg-[#0F172A]',
        dp.isElapsed ? 'opacity-40' : '',
      ].join(' ')}
    >
      <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-ink-500 dark:text-[#94A3B8]">
        {dp.label}
      </div>
      <Droplets className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
      <div className="text-sm font-semibold text-ink-900 dark:text-white">
        {dp.peakPct !== null ? `${dp.peakPct}%` : '—'}
      </div>
      <div className="h-3 text-[9px] uppercase tracking-wide text-ss-blue dark:text-blue-300">
        {sub}
      </div>
    </div>
  );
}

function PollutantTile({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | null;
  unit: string;
}) {
  return (
    <div className="rounded-lg bg-ink-50 px-1.5 py-2 dark:bg-[#1E293B]">
      <div className="text-[10px] uppercase tracking-wide text-ink-500 dark:text-[#94A3B8]">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-ink-900 dark:text-white">
        {value !== null ? value : '—'}
      </div>
      <div className="text-[10px] text-ink-500 dark:text-[#94A3B8]">{unit}</div>
    </div>
  );
}
